import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import OrganizationFormModal from './OrganizationFormModal';
import { useNavigate } from 'react-router-dom';

interface StatisticsSectionProps {
  signatureCount?: number;
}

export default function StatisticsSection({
  signatureCount = 0,
}: StatisticsSectionProps) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total: 0,
    byOccupation: [] as { name: string; value: number }[],
    byRegion: [] as { name: string; value: number }[],
    organizations: [] as { name: string; type: string; count: number }[],
    list100: [] as { name: string; affiliation: string }[],
    overseasCount: 0,
  });

  const [animatedStats, setAnimatedStats] = useState({
    byOccupation: [] as { name: string; value: number }[],
    byRegion: [] as { name: string; value: number }[],
  });

  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [showOrgForm, setShowOrgForm] = useState(false);

  const participatingOrganizations = [
    '1776연구소',
    '거룩한방파제 국토순례단',
    '건강한가정만들기국민운동본부',
    '광주바른교육시민연합',
    '교육개혁청년행동',
    '교육바로세우기운동본부',
    '교회자유수호연대',
    '노동개혁청년행동',
    '대한민국광역기독교총연합회',
    '리빌드코리아',
    '바른교육학부모연합',
    '바른청년연합',
    '바름누리',
    '보편가치연대',
    '새로운청년연합회',
    '서울기독교총연합회',
    '연금개혁청년행동',
    '자유변호사협회',
    '좋은교육시민모임',
    '청년행동',
    '청주자유시민연합',
    '충북자유민주시민연합',
    '충북자유아카데미',
    '트루스포럼',
    '행동하는학부모연합',
    '행크연구소',
  ];

  const fetchStatistics = useCallback(async () => {
    try {
      const { data: signers, error: signersError } = await supabase
        .from('signers')
        .select('*')
        .order('created_at', { ascending: true });

      if (signersError) throw signersError;

      const { data: orgs, error: orgsError } = await supabase
        .from('organizations')
        .select('*')
        .order('member_count', { ascending: false });

      if (orgsError) throw orgsError;

      if (signers) {
        // 빈 이름/이메일 레코드 필터링 & 이메일 기준 중복 제거
        const seenEmails = new Set<string>();
        const uniqueSigners = signers.filter((signer) => {
          if (!signer.name || !signer.name.trim()) return false;
          if (!signer.email || !signer.email.trim()) return false;
          const emailKey = signer.email.trim().toLowerCase();
          if (seenEmails.has(emailKey)) return false;
          seenEmails.add(emailKey);
          return true;
        });

        const occupationMap = new Map<string, number>();
        const regionMap = new Map<string, number>();
        const list100Members: { name: string; affiliation: string }[] = [];
        const seen100Names = new Set<string>();
        let overseasCount = 0;

        uniqueSigners.forEach((signer) => {
          if (signer.occupation) {
            occupationMap.set(
              signer.occupation,
              (occupationMap.get(signer.occupation) || 0) + 1,
            );
          }
          if (signer.region) {
            const isOverseas = signer.region.startsWith('해외');
            if (isOverseas) {
              overseasCount += 1;
            }
            regionMap.set(
              signer.region,
              (regionMap.get(signer.region) || 0) + 1,
            );
          }
          // 실명 공개 서명자: hide_name이 false인 사람
          if (!signer.hide_name) {
            const nameKey = `${signer.name.trim()}_${(signer.affiliation || '').trim()}`;
            if (!seen100Names.has(nameKey)) {
              seen100Names.add(nameKey);
              const displayAffiliation = signer.hide_affiliation
                ? ''
                : (signer.affiliation || '').trim();
              list100Members.push({
                name: signer.name.trim(),
                affiliation: displayAffiliation,
              });
            }
          }
        });

        // 단체 중복 제거 (이름 기준)
        const uniqueOrgs: { name: string; type: string; count: number }[] = [];
        const seenOrgNames = new Set<string>();
        if (orgs) {
          orgs.forEach((org) => {
            const orgNameKey = org.name.trim();
            if (!orgNameKey) return;
            if (seenOrgNames.has(orgNameKey)) {
              // 이미 있으면 member_count 합산
              const existing = uniqueOrgs.find((o) => o.name === orgNameKey);
              if (existing) {
                existing.count += org.member_count || 0;
              }
            } else {
              seenOrgNames.add(orgNameKey);
              uniqueOrgs.push({
                name: orgNameKey,
                type: org.type,
                count: org.member_count || 0,
              });
            }
          });
        }

        setStats({
          total: uniqueSigners.length,
          byOccupation: Array.from(occupationMap.entries())
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value),
          byRegion: Array.from(regionMap.entries())
            .map(([name, value]) => ({ name, value }))
            .filter((item) => !item.name.startsWith('해외'))
            .sort((a, b) => b.value - a.value),
          organizations: uniqueOrgs,
          list100: list100Members,
          overseasCount,
        });
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, []);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  // 카운트업 애니메이션
  useEffect(() => {
    if (hasAnimated || stats.byOccupation.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setHasAnimated(true);
          
          // 초기값 설정
          setAnimatedStats({
            byOccupation: stats.byOccupation.map(item => ({ ...item, value: 0 })),
            byRegion: stats.byRegion.map(item => ({ ...item, value: 0 })),
          });

          // 애니메이션 실행
          const duration = 1500;
          const steps = 60;
          const stepDuration = duration / steps;

          let currentStep = 0;
          const interval = setInterval(() => {
            currentStep++;
            const progress = currentStep / steps;

            setAnimatedStats({
              byOccupation: stats.byOccupation.map(item => ({
                ...item,
                value: Math.round(item.value * progress),
              })),
              byRegion: stats.byRegion.map(item => ({
                ...item,
                value: Math.round(item.value * progress),
              })),
            });

            if (currentStep >= steps) {
              clearInterval(interval);
              setAnimatedStats({
                byOccupation: stats.byOccupation,
                byRegion: stats.byRegion,
              });
            }
          }, stepDuration);

          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [stats, hasAnimated]);

  const displayOccupation = hasAnimated ? animatedStats.byOccupation : stats.byOccupation;
  const displayRegion = hasAnimated ? animatedStats.byRegion : stats.byRegion;

  const maxOccupation = Math.max(
    ...stats.byOccupation.map((o) => o.value),
    1,
  );

  const maxRegion = Math.max(
    ...stats.byRegion.map((r) => r.value),
    1,
  );

  return (
    <section id="statistics" className="py-12 md:py-20 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 섹션 타이틀 */}
        <div className="text-center mb-10 md:mb-14">
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4"
            style={{ fontFamily: "'Nanum Myeongjo', serif" }}
          >
            실시간 참여 현황
          </h2>
          <div className="w-24 h-1 bg-red-600 mx-auto mb-4 md:mb-6"></div>
          <p className="text-base md:text-lg text-gray-500">
            서명 참여자 통계를 실시간으로 확인하세요
          </p>
        </div>

        {/* 지역별 참여 현황 */}
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 md:p-8 mb-8 md:mb-12">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-map-pin-2-line text-base text-amber-500"></i>
            </div>
            <h3 className="text-base font-bold text-gray-800">지역별 참여 현황</h3>
          </div>

          {/* 해외 서명자 별도 표시 */}
          {stats.overseasCount > 0 && (
            <div className="flex items-center justify-between bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl px-4 py-3 mb-5">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-earth-line text-sm text-red-500"></i>
                </div>
                <span className="text-sm font-semibold text-red-700">해외 서명자</span>
              </div>
              <span className="text-lg font-extrabold text-red-600">
                {stats.overseasCount.toLocaleString()}명
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
            {(displayRegion.length > 0
              ? displayRegion
              : [
                  { name: '경기', value: 0 },
                  { name: '서울', value: 0 },
                  { name: '부산', value: 0 },
                  { name: '인천', value: 0 },
                  { name: '경남', value: 0 },
                  { name: '대구', value: 0 },
                  { name: '경북', value: 0 },
                  { name: '충남', value: 0 },
                  { name: '대전', value: 0 },
                  { name: '강원', value: 0 },
                ]
            ).map((item) => {
              const barWidth = maxRegion > 0 ? (item.value / maxRegion) * 100 : 0;
              return (
                <div key={item.name} className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 w-14 shrink-0 text-right">
                    {item.name}
                  </span>
                  <div className="flex-1 bg-gray-200 rounded-full h-5 overflow-hidden">
                    <div
                      className="bg-red-500 h-5 rounded-full transition-all duration-700"
                      style={{ width: `${Math.max(barWidth, 1)}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-bold text-gray-800 w-20 shrink-0 text-right whitespace-nowrap">
                    {item.value.toLocaleString()}명
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {showOrgForm && (
          <OrganizationFormModal onClose={() => setShowOrgForm(false)} />
        )}

        {/* CTA 버튼 */}
        <div className="mt-10 text-center">
          <button
            onClick={() => navigate('/sign')}
            className="inline-flex items-center gap-2 px-10 py-4 bg-red-600 text-white rounded-full text-base font-bold hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap shadow-xl"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-pen-nib-line text-lg"></i>
            </span>
            나도 서명에 참여하기
          </button>
        </div>
      </div>
    </section>
  );
}
